interface ApplicantPrivacyNoticeContentProps {
  backgroundColor?: string;
}

export function ApplicantPrivacyNoticeContent({
  backgroundColor,
}: ApplicantPrivacyNoticeContentProps = {}) {
  return (
    <div
      className={`prose prose-sm dark:prose-invert max-w-none space-y-12 ${backgroundColor || ""}`}
    >
      <section>
        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              1. Who we are
            </h3>
            <p>
              Symbolica AI Limited (&ldquo;Symbolica&rdquo;, &ldquo;we&rdquo;,
              &ldquo;us&rdquo;) is the data controller responsible for the
              personal data we collect and process during the recruitment
              process. Our registered office is at 66 City Road, Fourth Floor
              East, London EC1Y 2AL, United Kingdom.
            </p>
            <p>
              If you have questions about this notice or how we handle your
              data, please contact us at{" "}
              <a
                href="mailto:corporate@symbolica.ai"
                className="hover:text-foreground underline"
              >
                corporate@symbolica.ai
              </a>
              .
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              2. What personal data we collect
            </h3>
            <p>
              During the recruitment process we may collect and process the
              following categories of personal data:
            </p>
            <div className="space-y-3 pl-4">
              <p>
                (a) Identity and contact information: name, address, email,
                phone number.
              </p>
              <p>
                (b) Application information: CV, cover letter, work history,
                qualifications, references.
              </p>
              <p>
                (c) Interview records: notes taken during interviews, assessment
                scores, and interviewer feedback.
              </p>
              <p>
                (d) Right to work information: proof of eligibility to work in
                the UK.
              </p>
              <p>
                (e) Background check information: results of pre-employment
                screening, including DBS checks or equivalent, where applicable
                to the role.
              </p>
              <p>
                (f) Criminal offence data: where voluntarily disclosed by you or
                obtained through a background check.
              </p>
              <p>
                (g) Equal opportunities data: information about your gender,
                ethnicity, disability status, or other characteristics,
                collected on a voluntary basis for monitoring purposes only and
                not used in recruitment decisions. We may also use information
                you provide about any disability to make reasonable adjustments
                to our recruitment process.
              </p>
              <p>
                (h) References: information provided by your nominated referees,
                including their assessment of your suitability for the role.
              </p>
              <p>
                (i) Publicly available information: information from online
                sources, including professional networking sites and social
                media profiles, where relevant to verifying your identity or
                assessing your application.
              </p>
              <p>
                (j) Interview recordings and AI-generated notes: audio or video
                recordings of interviews and notes or summaries generated by
                AI-powered tools used during the interview process. All hiring
                decisions are made by people.
              </p>
              <p>
                (k) Technical data: information generated through your
                interaction with our application systems.
              </p>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              3. How we collect your data
            </h3>
            <p>We collect personal data from the following sources:</p>
            <div className="space-y-3 pl-4">
              <p>
                (a) Directly from you, through your application, correspondence,
                and interviews.
              </p>
              <p>
                (b) From recruitment platforms and job boards where you have
                submitted an application.
              </p>
              <p>(c) From referees whose details you have provided.</p>
              <p>
                (d) From background check providers, where a check is carried
                out as part of a conditional offer.
              </p>
              <p>
                (e) From publicly available online sources, including
                professional networking sites and social media, where relevant
                to verifying your identity or assessing your application.
              </p>
              <p>
                (f) From AI-powered tools used during interviews, which may
                generate automated notes or summaries of conversations.
              </p>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              4. Why we process your data and our lawful basis
            </h3>
            <p>We process your personal data for the following purposes:</p>
            <div className="mt-4 space-y-4">
              <div>
                <p>
                  <strong>Purpose:</strong> Assessing your application and
                  suitability for the role.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(b) -- steps
                  necessary prior to entering into a contract.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Conducting interviews and evaluating
                  candidates.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(b) -- steps
                  necessary prior to entering into a contract.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Carrying out background and
                  reference checks.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(b) --
                  pre-contractual steps; and Article 6(1)(f) -- our legitimate
                  interest in verifying candidate information and maintaining a
                  safe workplace.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Verifying candidate identity through
                  publicly available online sources.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(f) -- our
                  legitimate interest in confirming that applicants are genuine,
                  preventing fraud, and protecting against identity
                  misrepresentation.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Processing criminal offence data
                  obtained through background checks.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(f) -- legitimate
                  interests; Schedule 1, Part 1, Paragraph 1 DPA 2018 --
                  employment purposes. An Appropriate Policy Document is
                  maintained.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Processing criminal offence data
                  voluntarily disclosed by you.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 9(2)(a) -- explicit
                  consent.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Verifying right to work in the UK.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(c) -- compliance
                  with a legal obligation.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Equal opportunities monitoring.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 9(2)(j) -- statistical
                  and research purposes. Data is anonymised and not used in
                  individual recruitment decisions.
                </p>
              </div>
              <div>
                <p>
                  <strong>Purpose:</strong> Defending or establishing legal
                  claims.
                </p>
                <p>
                  <strong>Lawful basis:</strong> Article 6(1)(f) -- legitimate
                  interests; Article 9(2)(f) -- establishment, exercise or
                  defence of legal claims.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              5. Who we share your data with
            </h3>
            <p>
              We may share your personal data with the following categories of
              recipients where necessary:
            </p>
            <div className="space-y-3 pl-4">
              <p>(a) Background check and screening providers.</p>
              <p>
                (b) Our applicant tracking system provider (currently Ashby).
              </p>
              <p>
                (c) AI-powered interview tools used to generate notes or
                summaries during the recruitment process.
              </p>
              <p>
                (d) Professional advisers, including legal counsel, where
                required.
              </p>
              <p>
                (e) Regulatory or government bodies where we are required to do
                so by law.
              </p>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              6. International transfers
            </h3>
            <p>
              Symbolica operates in both the UK and the United States. Your
              personal data may be transferred to and processed in the United
              States by Symbolica AI, Inc., our US parent company, where
              necessary for the recruitment process. Where we transfer personal
              data outside the UK, we ensure appropriate safeguards are in
              place, including standard contractual clauses, reliance on an
              adequacy decision or an appropriate derogation, as applicable.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              7. How long we keep your data
            </h3>
            <p>
              If your application is unsuccessful, we will retain your personal
              data for 6 months after the conclusion of the recruitment process,
              after which it will be securely deleted. If a longer retention
              period is necessary to defend against a legal claim, data will be
              retained until that claim is resolved.
            </p>
            <p>
              If you are offered and accept a role, relevant data from your
              application will be transferred to your personnel file and
              retained in accordance with our employee privacy notice.
            </p>
            <p>
              If you consent to us keeping your details on file for future
              opportunities, we will retain your data for up to 12 months and
              then securely delete it unless you renew your consent.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              8. Automated decision-making
            </h3>
            <p>
              We do not make recruitment decisions based solely on automated
              processing. We use AI-powered tools during interviews to assist
              with note-taking and generating summaries. These tools support our
              interviewers but do not score, rank, or assess candidates. All
              hiring decisions are made by people. You may request a copy of any
              AI-generated notes relating to your interviews.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              9. Your rights
            </h3>
            <p>In certain circumstances, you may have the right to:</p>
            <div className="space-y-3 pl-4">
              <p>(a) Access the personal data we hold about you.</p>
              <p>
                (b) Request correction of inaccurate or incomplete personal
                data.
              </p>
              <p>(c) Request erasure of your personal data.</p>
              <p>(d) Restrict or object to certain types of processing.</p>
              <p>
                (e) Data portability -- receive your data in a structured,
                commonly used format.
              </p>
              <p>
                (f) Withdraw consent at any time, where processing is based on
                consent.
              </p>
              <p>
                (g) Make a complaint to us regarding the processing of your
                personal data.
              </p>
            </div>
            <p className="mt-4">
              To exercise any of these rights, please contact{" "}
              <a
                href="mailto:corporate@symbolica.ai"
                className="hover:text-foreground underline"
              >
                corporate@symbolica.ai
              </a>
              .
            </p>
            <p>
              You also have the right to make a complaint to the Information
              Commissioner&apos;s Office (
              <a
                href="https://ico.org.uk"
                className="hover:text-foreground underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                ico.org.uk
              </a>
              ) if you believe your personal data has been handled unlawfully.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              10. Changes to this notice
            </h3>
            <p>
              We may update this notice from time to time. The latest version
              will always be available on request and via our careers page.
            </p>
          </section>
        </div>
      </section>
    </div>
  );
}
